/**
 * @file        src/components/DocBrandMark.jsx
 * @description The issuing brand mark printed at the top of every admin-side
 *              document (flight invoice + e-ticket, hotel invoice + voucher).
 *              One component so the four documents can never drift apart on
 *              which logo they show or in what order they look for it.
 *
 *              Resolution order, most specific first:
 *                1. the BOOKING's own storefront logo (brands.logo_url) — a
 *                   reprint must carry the mark the customer's copy carries;
 *                2. this TENANT's admin-portal logo (site_settings.logo_url) —
 *                   a different branding surface with a different owner
 *                   (CLAUDE.md §7), but still this tenant's own mark, so it is a
 *                   truthful stand-in rather than another tenant's;
 *                3. the bundled BrandWordmark SVG.
 *              A configured URL that fails to load falls to the SVG too, so a
 *              dead link never prints a broken-image box on a tax invoice.
 *
 *              The four documents previously inlined this chain themselves and
 *              stopped at step 1, falling back to COMPANY.legal as PLAIN TEXT.
 *              Neither logo column is populated on most tenants, so in practice
 *              every printed invoice / voucher / e-ticket showed a text wordmark
 *              where the customer's copy showed the real logo.
 * @author      Kinetika Labs
 * @created     2026-09-09
 * @changelog
 *   - 2026-09-09 Claude — Initial. Extracted from the four *Doc.jsx pages, with
 *                          the admin-portal logo and the SVG wordmark added
 *                          below the storefront logo as fallbacks.
 */
import { useState } from 'react';
import { useBrands } from '../api/brands';
import { useBranding } from '../lib/useBranding';
import BrandWordmark from './BrandWordmark';

/**
 * @param {{brandCode?: string, className?: string, style?: object, alt?: string}} props
 * `brandCode` is the booking's `brand` (3-char storefront code). `className` is
 * applied to whichever element renders — `doc-brand-logo` on the invoices,
 * `tkt-brand-logo` on the e-ticket — so admin.css sizes both the <img> and the
 * <svg> from one rule.
 */
export default function DocBrandMark({ brandCode, className = 'doc-brand-logo', style, alt }) {
  const { brands } = useBrands();
  const { logoUrl } = useBranding();
  // A URL that 404s must not leave a broken image on the sheet; once it fails we
  // render the SVG instead, which needs state rather than an onError src swap
  // (the fallback is not an image).
  const [urlFailed, setUrlFailed] = useState(false);

  const brandRow = brands.find((x) => x.code === brandCode) || null;
  const src = brandRow?.logo_url || logoUrl || null;
  const label = alt || brandRow?.name || 'Neon Holidays';

  if (!src || urlFailed) {
    return <BrandWordmark className={className} style={style} title={label} />;
  }
  return (
    <img
      className={className}
      style={style}
      src={src}
      alt={label}
      onError={() => setUrlFailed(true)}
    />
  );
}
